import { useSeoTags } from "../useSeoTags";
import "../marketMyths.css";
import "../humanArticleTemplate.css";

// TRACK B, 2026-10-25 -- standalone humanized article for the FPI-cyclicity
// finding, promoted out of being a cross-link-only entry on the
// index-direction doc (§7.2) into its own page. Flipped live 2026-10-25,
// dispatched from MarketMythsPage.tsx's TEMPLATED_EVIDENCE map at
// /market-myths?evidence=fpi-cyclicity -- same embeddable-component shape
// as the other 6 static evidence docs (see e.g. Phase3SweepArticlePage.tsx).
//
// HARD CONSTRAINT, load-bearing: this page shows ONLY our own DERIVED
// statistics from §7.2 (correlations, p-values, test counts, the
// Bonferroni threshold) -- ZERO raw NSDL flow figures anywhere (no flow
// values, no flow table, no flow chart). NSDL's own site terms prohibit
// "reproduction, redistribution and transmission of any information
// contained on the website" -- confirmed live, current, on
// fpi.nsdl.co.in's own footer disclaimer, 2026-10-25. A correlation
// coefficient or a p-value computed BY US from that data is our own
// derived analytical output, not a reproduction of NSDL's published
// numbers -- that's the line this page holds. If a future edit would let
// a reader back out an actual flow figure (a value, a delta, a level),
// it does not belong on this page.
//
// Every number below verified against
// docs/research/nse_index_direction_nonforecastability.md §7.2
// (lines 167-173) before writing.
//
// CHART: a p-value-vs-Bonferroni-threshold comparison, built from our own
// three best-nominal test results. All three values plotted are p-values
// and a correction threshold -- our computed statistics, never a flow
// number.

const CANONICAL = "https://tickerpicker.in/market-myths?evidence=fpi-cyclicity";

const BONFERRONI_ALPHA = 0.0019;

const BEST_NOMINAL: { label: string; corr: string; p: number }[] = [
  { label: "Bank Nifty", corr: "+0.192", p: 0.023 },
  { label: "NIFTY Infra", corr: "+0.154", p: 0.018 },
  { label: "NIFTY Media", corr: "+0.134", p: 0.042 },
];

function PValueChart() {
  const width = 640;
  const rowH = 44;
  const topPad = 14;
  const height = BEST_NOMINAL.length * rowH + topPad + 34;
  const labelW = 110;
  const plotW = width - labelW - 90;

  // Log scale on -log10(p): larger bar = smaller p = more significant.
  // Axis spans from a bit past the Bonferroni line (most significant
  // required point) down to a bit past the weakest result, so every
  // real value and the threshold line both land inside the frame.
  const toX = (p: number) => -Math.log10(p);
  const maxX = toX(BONFERRONI_ALPHA) * 1.12;
  const minX = 0;
  const scale = plotW / (maxX - minX);
  const thresholdX = labelW + (toX(BONFERRONI_ALPHA) - minX) * scale;

  return (
    <figure className="hatpl-chart">
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img"
           aria-label="The three best nominal FPI-flow-vs-sector-rank correlations, each far short of the Bonferroni-corrected significance threshold">
        <line x1={thresholdX} y1={6} x2={thresholdX} y2={height - 26} className="hatpl-chart-zeroline" />
        <text x={thresholdX} y={height - 10} textAnchor="middle" className="hatpl-chart-label">
          needed: p &le; 0.0019
        </text>
        {BEST_NOMINAL.map((d, i) => {
          const y = topPad + i * rowH;
          const x = labelW + (toX(d.p) - minX) * scale;
          const w = x - labelW;
          return (
            <g key={d.label}>
              <text x={0} y={y + 14} className="hatpl-chart-label">{d.label}</text>
              <rect x={labelW} y={y + 2} width={Math.max(w, 1)} height={16} className="hatpl-chart-bar-neg" />
              <text x={x + 8} y={y + 14} className="hatpl-chart-value">
                r={d.corr}, p={d.p}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption>
        Longer bar = smaller p-value = closer to significant. The dashed line marks where a result would need
        to reach to survive testing 26 sector&times;horizon combinations at once. None of the three closest
        results get near it &mdash; the gap on the page is roughly the same size as the gap in the math.
      </figcaption>
      <div className="hatpl-asof">
        As of 2026&#8209;09&#8209;12 (frozen analysis, §7.2) &middot; not a live recompute.
      </div>
    </figure>
  );
}

export function FpiCyclicityArticlePage({ onBack }: { onBack: () => void }) {
  useSeoTags({
    title: "Everyone watches where the foreign money goes. It doesn't tell you where it's going next. — Tickerpicker",
    description:
      "Fortnightly foreign portfolio flow by sector is public and widely watched. We tested whether it forecasts " +
      "which sector's relative rank improves next. Across 26 tests, zero survive correction.",
    canonical: CANONICAL,
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Everyone watches where the foreign money goes. It doesn't tell you where it's going next.",
      url: CANONICAL,
      publisher: { "@type": "Organization", name: "Tickerpicker" },
      isPartOf: { "@type": "WebPage", name: "Market Myths: Tested", url: "https://tickerpicker.in/market-myths" },
    },
  });

  return (
    <div className="hatpl-shell">
      <nav className="hatpl-nav">
        <a href="/" className="hatpl-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
          <a href="/">Home</a>
          <span className="mm-crumb-sep">/</span>
          <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
        </div>
      </nav>

      <article className="hatpl-article">
        <div className="hatpl-kicker">Market Myths: Tested — FPI sector flows</div>
        <h1>Everyone watches where the foreign money goes. It doesn't tell you where it's going next.</h1>

        <p>
          Every two weeks, foreign portfolio investors' sector-level buying and selling becomes public, and a lot
          of market commentary treats that number as an early tell — a sector getting fresh foreign money today
          is supposedly a sector worth watching tomorrow. We tested whether that's actually true: does fortnightly
          FPI sector flow forecast which sector's relative ranking improves next. It doesn't.
        </p>

        <p>
          This sits inside a broader investigation into whether Indian market indices show any forecastable
          rotation at all, which has its own separate finding (linked below). NIFTY and BSE SENSEX are the two
          broad-market benchmarks the companion investigation also covers, but neither is a sector, so neither
          belongs in this question at all — this is strictly about the 15 NSE sector indices. Of those 15, 14
          have a distinct NSDL flow label mapped to them (one, NIFTY PSU Bank, has no mapping of its own). Of
          those 14, 13 had enough actual flow observations to test — the 14th's mapped label never showed up
          with a usable value in the data captured. For those 13 sectors, we checked whether a sector's flow in
          one fortnight correlates with that sector's relative rank improving one or two weeks later: 13 sectors
          times 2 horizons, 26 separate tests, frozen as of 2026&#8209;09&#8209;12. Run 26 tests and some will
          look significant by chance alone, so we corrected for that properly — a Bonferroni-adjusted bar of
          p ≤ 0.0019, not the usual 0.05, before any single result counts for anything.
        </p>

        <PValueChart />

        <p>
          Zero of the 26 clear it. The three closest: Bank Nifty's flow-to-rank correlation, nominally significant
          on its own terms (p = 0.023); NIFTY Infra's (p = 0.018); NIFTY Media's (p = 0.042). Real numbers, and
          not nothing in isolation — but nowhere near what a 26-test family actually requires. It isn't that a
          real effect is hiding just under a conservative correction, either. Across all 26 results, roughly half
          come back positive and half negative — the signature of noise scattering around zero, not a muted real
          pattern waiting to be uncovered with more data.
        </p>

        <blockquote className="hatpl-pullquote">
          A number that would pass as significant on its own fails the moment you ask how many numbers you tested.
        </blockquote>

        <p>
          This doesn't say FPI flow is meaningless to markets generally. Whether foreign flow relates to
          broader, index-level or emerging-market-level returns is a separate question this analysis doesn't
          address one way or the other — we tested sector-level rotation specifically, not that. And sector
          rotation itself is a real, well-documented phenomenon in equity markets. What this result says is
          narrower: fortnightly FPI sector flow, attributed to sectors through
          an audited mapping and dated by its actual publication date rather than the period it describes, does
          not forecast which sector's rank improves next at a one- or two-week horizon, once you account for
          testing 26 things at once.
        </p>

        <p>
          This is one piece of a larger look at index and sector rotation — the rank-structure half of that
          question runs live and updates weekly; see the full{" "}
          <a href="/market-myths?evidence=index-direction">index-direction investigation</a> for the rest of it.
        </p>

        <hr className="hatpl-rule" />

        <details className="hatpl-numbers">
          <summary>The full numbers</summary>
          <table>
            <tbody>
              <tr><td>NSE sector indices (total)</td><td>15</td></tr>
              <tr><td>NIFTY &amp; BSE SENSEX</td><td>excluded entirely — broad-market benchmarks, not sectors, not part of this count</td></tr>
              <tr><td>Of the 15 sectors, with a distinct NSDL flow-label mapping</td><td>14 (NIFTY PSU Bank has no distinct crosswalk target)</td></tr>
              <tr><td>Of those 14, with sufficient non-null flow data to test</td><td>13 (the 14th's mapped label never had a usable value)</td></tr>
              <tr><td>Sectors actually tested</td><td>13</td></tr>
              <tr><td>Horizons tested</td><td>1-week and 2-week forward relative-rank change</td></tr>
              <tr><td>Analysis frozen as of</td><td>2026&#8209;09&#8209;12 — not updated since</td></tr>
              <tr><td>Total tests</td><td>26 (13 sectors &times; 2 horizons)</td></tr>
              <tr><td>Bonferroni-corrected &alpha;</td><td>0.05 / 26 = 0.0019</td></tr>
              <tr><td>Tests surviving correction</td><td>0 of 26</td></tr>
              <tr><td>Sign pattern across all 26</td><td>roughly half positive, half negative — consistent with noise</td></tr>
              <tr><td>Best nominal — Bank Nifty</td><td>r = +0.192, p = 0.023</td></tr>
              <tr><td>Best nominal — NIFTY Infra</td><td>r = +0.154, p = 0.018</td></tr>
              <tr><td>Best nominal — NIFTY Media</td><td>r = +0.134, p = 0.042</td></tr>
              <tr><td>Flow dating convention</td><td>dated by actual publication date (lags the period described by 3&ndash;6 days), never the period-end date</td></tr>
            </tbody>
          </table>
          <p className="hatpl-source">
            Source: <code>docs/research/nse_index_direction_nonforecastability.md</code> §7.2, produced by{" "}
            <code>backend/index_rotation_hunt.py</code>. Underlying fortnightly flow data is sourced from NSDL;
            per NSDL's own published terms, reproduction and redistribution of that data is not permitted, so
            only our own derived statistics (correlations, p-values, test counts) appear on this page — no flow
            figures, tables, or charts of flows.
          </p>
        </details>

        <div className="mm-evid-footnav">
          <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
        </div>
      </article>
    </div>
  );
}
